import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ClubProfile } from '../utils/interfaces';
import { CLUBS_ORDER } from '../utils/constants';
import API_BASE_URL from '../utils/config';
import './PublicProfile.css';

// A generic interface for the user profile, as the structure is dynamic.
interface PublicUserProfile {
    username: string;
    [key: string]: any; // Allow any other properties
}

const PublicProfile: React.FC = () => {
    const { username } = useParams<{ username: string }>();
    const [profile, setProfile] = useState<PublicUserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dmOpen, setDmOpen] = useState(false);
    const [recipient, setRecipient] = useState('');
    const [dmMessage, setDmMessage] = useState('');
    const [toast, setToast] = useState<string | null>(null);
    const [activeMetric, setActiveMetric] = useState<{ club: string; key: string } | null>(null);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/user_profile/${username}`);
                
                if (!response.ok) {
                    if (response.status === 404) {
                        setError('User not found.');
                    } else {
                        throw new Error('Failed to fetch user profile.');
                    }
                } else {
                    const data = await response.json();
                    // The 'profile' field is a stringified JSON, so we need to parse it.
                    if (data.profile && typeof data.profile === 'string') {
                        try {
                            // Parse the stringified JSON into an object
                            data.profile = JSON.parse(data.profile);
                        } catch (e) {
                            console.error("Failed to parse nested profile JSON:", e);
                            // If parsing fails, we can leave it as a string, but it won't be pretty.
                        }
                    }
                    setProfile(data);
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An unknown error occurred.');
            } finally {
                setLoading(false);
            }
        };

        if (username) {
            fetchProfile();
        }
    }, [username]);

    if (loading) {
        return <div className="public-profile-container">Loading profile...</div>;
    }

    if (error) {
        return <div className="public-profile-container error">{error}</div>;
    }

    if (!profile || !profile.profile) {
        return <div className="public-profile-container">No profile data available.</div>;
    }

    // Extract club names from the profile data, ordered to match player profile
    const profileClubs = profile.profile || {};
    const playerClubs = CLUBS_ORDER.filter((clubKey) => profileClubs[clubKey]).concat(
        Object.keys(profileClubs).filter((key) =>
            !CLUBS_ORDER.includes(key) &&
            ![
                'general', 'measurements', 'Ball', 'driverDistance',
                'sevenIronDistance', 'pitchingWedgeDistance'
            ].includes(key)
        )
    );

    const getAdvancedItems = (clubDetails: ClubProfile) => {
        const raw = clubDetails as any;
        const items = [
            { key: 'distance', label: 'Carry', value: clubDetails.distance ?? raw.carry ?? raw.carry_distance },
            { key: 'launch_angle', label: 'Launch Angle', value: raw.launch_angle ?? raw.launchAngle },
            { key: 'spin_rate', label: 'Spin Rate', value: raw.spin_rate ?? raw.spinRate },
            { key: 'descent_angle', label: 'Descent Angle', value: raw.descent_angle ?? raw.descentAngle }
        ];

        return items;
    };

    const formatMetricValue = (key: string, value: any) => {
    if (value === null || value === undefined || value === '') return '—';
        if (typeof value === 'number') {
            if (['launch_angle', 'descent_angle'].includes(key)) return `${value}°`;
            if (key === 'spin_rate') return `${value} rpm`;
            if (key === 'distance') return `${value} yd`;
        }
        return `${value}`;
    };

    const renderMetricIcon = (key: string) => {
        switch (key) {
            case 'distance':
                return (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
                    </svg>
                );
            case 'launch_angle':
                return (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M4 20L20 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        <path d="M14 4h6v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                );
            case 'descent_angle':
                return (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M4 4L20 20" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        <path d="M14 20h6v-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                );
            case 'spin_rate':
                return (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M12 4a8 8 0 1 1-7.2 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        <path d="M4 8h4V4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                );
            default:
                return (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <rect x="4" y="4" width="16" height="16" rx="4" stroke="currentColor" strokeWidth="1.6" />
                        <path d="M8 12h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                );
        }
    };

    return (
        <div className="public-profile-container">
            <div className="profile-card">
                <div className="profile-card-main">
                    <h2 className="profile-username">@{profile.username}</h2>
                    {playerClubs.length > 0 ? (
                        <ul className="profile-club-list">
                            {playerClubs.map(club => {
                                const clubDetails = profile.profile?.[club] as ClubProfile | undefined;
                                if (!clubDetails || !clubDetails.model) return null;

                                const advancedItems = getAdvancedItems(clubDetails);
                                const hasAdvanced = advancedItems.length > 0;

                                if (!hasAdvanced) {
                                    return (
                                        <li key={club} className="profile-club-item">
                                            <div className="club-item-name">{club}</div>
                                            <div className="club-item-model">{clubDetails.model}</div>
                                            <div className="club-item-specs">
                                                {clubDetails.shaft && <span>Shaft: {clubDetails.shaft}</span>}
                                                {clubDetails.loft && <span>Loft: {clubDetails.loft}°</span>}
                                                {clubDetails.length && <span>Length: {clubDetails.length}"</span>}
                                            </div>
                                        </li>
                                    );
                                }

                                return (
                                    <li key={club} className="profile-club-item has-advanced">
                                        <div className="club-item-left">
                                            <div className="club-item-name">{club}</div>
                                            <div className="club-item-model">{clubDetails.model}</div>
                                            <div className="club-item-specs">
                                                {clubDetails.shaft && <span>Shaft: {clubDetails.shaft}</span>}
                                                {clubDetails.loft && <span>Loft: {clubDetails.loft}°</span>}
                                                {clubDetails.length && <span>Length: {clubDetails.length}"</span>}
                                            </div>
                                        </div>
                                        <div className="club-item-advanced">
                                            <div className="advanced-list">
                                                {advancedItems.map(item => {
                                                    const isActive = activeMetric?.club === club && activeMetric?.key === item.key;
                                                    const displayText = isActive
                                                        ? item.label
                                                        : `${item.label}: ${formatMetricValue(item.key, item.value)}`;
                                                    return (
                                                        <button
                                                            key={`${club}-${item.key}`}
                                                            type="button"
                                                            className={`advanced-item ${isActive ? 'active' : ''}`}
                                                            onClick={() => setActiveMetric(isActive ? null : { club, key: item.key })}
                                                            aria-label={`${item.label} ${formatMetricValue(item.key, item.value)}`}
                                                        >
                                                            {renderMetricIcon(item.key)}
                                                            <span className="advanced-text">{displayText}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <p>No clubs listed in the profile.</p>
                    )}
                </div>

                {/* Share row */}
                <div className="share-row">
                    <button
                        className="share-button copy"
                        onClick={async () => {
                            try {
                                await navigator.clipboard.writeText(window.location.href);
                                setToast('Link copied to clipboard');
                                setTimeout(() => setToast(null), 2500);
                            } catch (e) {
                                setToast('Could not copy link');
                                setTimeout(() => setToast(null), 2500);
                            }
                        }}
                    >
                        Copy Link
                    </button>

                    <button
                        className="share-button dm"
                        onClick={async () => {
                            // Try native share first
                            const shareData = { title: `@${profile.username}`, text: `Check out @${profile.username}'s bag`, url: window.location.href };
                            if (navigator.share) {
                                try {
                                    await navigator.share(shareData);
                                    setToast('Shared via native share');
                                    setTimeout(() => setToast(null), 2500);
                                    return;
                                } catch (e) {
                                    // fallthrough to DM modal
                                }
                            }
                            // Open DM modal (Instagram-like)
                            setDmOpen(true);
                            setDmMessage(`Check out @${profile.username}'s bag: ${window.location.href}`);
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{marginRight:8}}>
                            <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Send
                    </button>
                </div>

                {/* DM modal */}
                {dmOpen && (
                    <div className="dm-modal-backdrop" onClick={() => setDmOpen(false)}>
                        <div className="dm-modal" onClick={(e) => e.stopPropagation()}>
                            <h3>Send to</h3>
                            <input placeholder="Recipient username" value={recipient} onChange={(e) => setRecipient(e.target.value)} />
                            <textarea rows={4} value={dmMessage} onChange={(e) => setDmMessage(e.target.value)} />
                            <div className="dm-actions">
                                <button className="btn-secondary" onClick={() => setDmOpen(false)}>Cancel</button>
                                <button
                                    className="btn-primary"
                                    onClick={() => {
                                        // In this app we don't have a messaging backend, so simulate send.
                                        // If you have a messaging API, replace this with a POST to that endpoint.
                                        if (!recipient) {
                                            setToast('Please enter a recipient');
                                            setTimeout(() => setToast(null), 2000);
                                            return;
                                        }
                                        // Simulate send
                                        setToast(`Sent to @${recipient}`);
                                        setTimeout(() => setToast(null), 2500);
                                        setDmOpen(false);
                                        setRecipient('');
                                    }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{marginRight:6}}>
                                        <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    Send
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Toast */}
                {toast && <div className="toast">{toast}</div>}
            </div>
        </div>
    );
};

export default PublicProfile;
